import { z } from 'zod';
import { DomainError } from '../errors.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Tedarikçi carisi (ADR 0119): tedarikçinin portaldan yüklediği fatura, ofisin incelemesi, ödeme
 * kaydı ve ters kaydı; para birimi başına bakiye, FIFO vade, ekstre ve geçmiş. Saf modül: I/O yok,
 * "bugün" parametreyle gelir. Tutarlar kanonik ondalık metindir (`"1200.00"`; ADR 0117 §11
 * kalıbı), hesap BigInt kuruşla yapılır; kayan nokta yok. Her para biriminde iki hane (ADR 0093
 * §2). Bu cari şirketin tedarikçiye borcudur; gemi kasası (ADR 0093/0110) değildir, gemiye inmez.
 */

/** Tam kısım en çok 13 hane (9.999.999.999.999,99). */
export const LEDGER_MAX_INTEGER_DIGITS = 13;
/** Faturada en çok vergi satırı. */
export const MAX_INVOICE_TAX_LINES = 5;
/** Fatura PDF'inin üst sınırı (ADR 0119 §2): 8 MB. */
export const MAX_INVOICE_PDF_BYTES = 8 * 1024 * 1024;
/** Yeni onaylanan banka sürümüne ilk ödemenin onayından önce bekleme (ADR 0119 §4; #1370). */
export const BANK_COOLING_OFF_HOURS = 24;
/** Fazla faturalama gerekçesi ve ters kayıt gerekçesinin en kısa uzunluğu. */
export const MIN_LEDGER_REASON = 10;
/** Ret notunun en kısa uzunluğu. */
export const MIN_REJECT_NOTE = 5;

const MONEY = /^(\d{1,13})(?:\.(\d{1,2}))?$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Kanonik ondalık metni kuruşa çevirir; geçersizse `null` (gruplama, eksi, üs, 3 ondalık yok). */
export function parseMoney(text: string): bigint | null {
  const match = MONEY.exec(text);
  if (!match) return null;
  return BigInt(match[1] ?? '0') * 100n + BigInt((match[2] ?? '').padEnd(2, '0'));
}

/** Kuruşu kanonik ondalık metne çevirir (`"1200.50"`, `"-0.05"`). */
export function moneyText(minor: bigint): string {
  const sign = minor < 0n ? '-' : '';
  const abs = minor < 0n ? -minor : minor;
  return `${sign}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}`;
}

/** Eski `real` tutarı (PO, GRN) kuruşa yuvarlar: önce 6 haneye, sonra yarım yukarı. */
export function minorOf(value: number): bigint {
  const scaled = Math.round(Math.round(value * 1e6) / 1e4);
  return BigInt(scaled);
}

/** Karşılaştırma için fatura numarası: büyük harf, boşluksuz (#1360). */
export function normalizeInvoiceNo(value: string): string {
  return value.normalize('NFC').toUpperCase().replace(/\s+/g, '');
}

function validDay(value: string): boolean {
  if (!DAY.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

/** İki gün arasındaki gün farkı (`b − a`). */
export function daysBetween(a: string, b: string): number {
  return Math.round(
    (Date.parse(`${b}T00:00:00.000Z`) - Date.parse(`${a}T00:00:00.000Z`)) / 86_400_000,
  );
}

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const requiredText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const moneyField = () =>
  z.string(typed('INVALID_AMOUNT')).transform((value, ctx) => {
    const minor = parseMoney(value.trim());
    if (minor === null) {
      ctx.addIssue({ code: 'custom', message: 'INVALID_AMOUNT' });
      return z.NEVER;
    }
    return minor;
  });
const dayField = () => z.string(typed('INVALID_DATE')).refine(validDay, 'INVALID_DATE');
const currencyField = () =>
  z.string(typed('INVALID_CURRENCY')).regex(/^[A-Z]{3}$/, 'INVALID_CURRENCY');
const rateField = () =>
  z
    .string(typed('INVALID_RATE'))
    .regex(/^\d{1,3}(?:\.\d{1,2})?$/, 'INVALID_RATE')
    .refine((value) => Number(value) <= 100, 'INVALID_RATE');

function issues(error: z.ZodError) {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    code: issue.message,
  }));
}

// --- Fatura ---

const taxLineSchema = z.strictObject({
  label: requiredText(40),
  rate: rateField(),
  amount: moneyField(),
});

const invoiceSchema = z.strictObject({
  invoiceNo: requiredText(40),
  issueDate: dayField(),
  dueDate: dayField(),
  currency: currencyField(),
  netAmount: moneyField(),
  taxLines: z.array(taxLineSchema).max(MAX_INVOICE_TAX_LINES, 'TOO_MANY_TAX_LINES').default([]),
  totalAmount: moneyField(),
  /** PO toplamını aşan faturalamanın gerekçesi (#1359). */
  overBillingReason: optionalText(1000),
  /** Reddedilen faturanın yerine (#1365). */
  replacesInvoiceId: z
    .uuid('INVALID_ID')
    .nullish()
    .transform((value) => value ?? null),
});

export interface InvoiceInput {
  invoiceNo: string;
  issueDate: string;
  dueDate: string;
  currency: string;
  net: bigint;
  taxLines: Array<{ label: string; rate: string; amount: bigint }>;
  tax: bigint;
  total: bigint;
  overBillingReason: string | null;
  replacesInvoiceId: string | null;
}

/**
 * Fatura girişi (ADR 0119 §2): net > 0, vergi satırları, toplam = net + vergi (sunucu yeniden
 * hesaplar, `INVOICE_TOTAL_MISMATCH`); fatura tarihi bugünden ileri olamaz (`FUTURE_DATE`), vade
 * fatura tarihinden önce olamaz (`DUE_BEFORE_ISSUE`).
 */
export function parseInvoiceInput(input: unknown, today: string): ParseResult<InvoiceInput> {
  const result = invoiceSchema.safeParse(input);
  if (!result.success) return { ok: false, issues: issues(result.error) };
  const value = result.data;
  const fail = (path: string, code: string): ParseResult<InvoiceInput> => ({
    ok: false,
    issues: [{ path, code }],
  });
  if (value.netAmount <= 0n) return fail('netAmount', 'OUT_OF_RANGE');
  if (value.issueDate > today) return fail('issueDate', 'FUTURE_DATE');
  if (value.dueDate < value.issueDate) return fail('dueDate', 'DUE_BEFORE_ISSUE');
  const tax = value.taxLines.reduce((sum, line) => sum + line.amount, 0n);
  if (value.netAmount + tax !== value.totalAmount)
    return fail('totalAmount', 'INVOICE_TOTAL_MISMATCH');
  return {
    ok: true,
    value: {
      invoiceNo: value.invoiceNo,
      issueDate: value.issueDate,
      dueDate: value.dueDate,
      currency: value.currency,
      net: value.netAmount,
      taxLines: value.taxLines.map((line) => ({
        label: line.label,
        rate: line.rate,
        amount: line.amount,
      })),
      tax,
      total: value.totalAmount,
      overBillingReason: value.overBillingReason,
      replacesInvoiceId: value.replacesInvoiceId,
    },
  };
}

/**
 * Faturalama sınırı (#1359): reddedilmemiş faturalar + yeni fatura PO toplamını aşarsa gerekçe
 * zorunlu (≥ 10 karakter). Tolerans sıfır.
 */
export function assertBilling(input: {
  poTotal: bigint;
  billed: bigint;
  amount: bigint;
  reason: string | null;
}): { over: boolean } {
  const over = input.billed + input.amount > input.poTotal;
  if (over && (input.reason ?? '').trim().length < MIN_LEDGER_REASON) {
    throw new DomainError('OVER_BILLING_REASON_REQUIRED');
  }
  return { over };
}

/** Faturanın içerik hash'ine giren kapalı liste (inceleme bu hash'i taşır). */
export function invoiceContent(input: {
  supplierId: string;
  purchaseOrderId: string;
  invoiceNo: string;
  issueDate: string;
  dueDate: string;
  currency: string;
  net: string;
  taxLines: ReadonlyArray<{ label: string; rate: string; amount: string }>;
  tax: string;
  total: string;
  overBillingReason: string | null;
  replacesInvoiceId: string | null;
  fileSha256: string;
}) {
  return {
    supplierId: input.supplierId,
    purchaseOrderId: input.purchaseOrderId,
    invoiceNo: input.invoiceNo,
    issueDate: input.issueDate,
    dueDate: input.dueDate,
    currency: input.currency,
    net: input.net,
    taxLines: input.taxLines.map((line) => ({
      label: line.label,
      rate: line.rate,
      amount: line.amount,
    })),
    tax: input.tax,
    total: input.total,
    overBillingReason: input.overBillingReason,
    replacesInvoiceId: input.replacesInvoiceId,
    fileSha256: input.fileSha256,
  };
}

// --- İnceleme ve üç yönlü eşleştirme ---

export const INVOICE_REVIEW_ACTIONS = ['approved', 'rejected'] as const;
export type InvoiceReviewAction = (typeof INVOICE_REVIEW_ACTIONS)[number];
export type InvoiceStatus = 'pending' | InvoiceReviewAction;

export const MATCH_WARNINGS = ['OVER_PO', 'OVER_RECEIVED', 'NOT_RECEIVED'] as const;
export type MatchWarning = (typeof MATCH_WARNINGS)[number];

export interface ThreeWayMatch {
  poTotal: bigint;
  received: bigint;
  invoiced: bigint;
  /** Faturalanmamış kalan (eksiye düşmez). */
  remaining: bigint;
  warnings: MatchWarning[];
}

/**
 * PO – GRN – fatura (ADR 0119 §3; #1366): faturalanan (bekleyen + onaylı) PO toplamını aşarsa
 * `OVER_PO`; teslim alınan değeri aşarsa `OVER_RECEIVED`; hiç teslim yoksa `NOT_RECEIVED`.
 */
export function threeWayMatch(input: {
  poTotal: bigint;
  received: bigint;
  invoiced: bigint;
}): ThreeWayMatch {
  const warnings: MatchWarning[] = [];
  if (input.invoiced > input.poTotal) warnings.push('OVER_PO');
  if (input.invoiced > 0n && input.received <= 0n) warnings.push('NOT_RECEIVED');
  else if (input.invoiced > input.received) warnings.push('OVER_RECEIVED');
  const remaining = input.poTotal - input.invoiced;
  return { ...input, remaining: remaining > 0n ? remaining : 0n, warnings };
}

const invoiceReviewSchema = z.strictObject({
  action: z.enum(INVOICE_REVIEW_ACTIONS, typed('INVALID_ACTION')),
  note: optionalText(1000),
  acknowledgeWarnings: z.boolean().optional(),
});
export type InvoiceReviewInput = z.input<typeof invoiceReviewSchema>;

export function parseInvoiceReview(
  input: unknown,
): ParseResult<z.output<typeof invoiceReviewSchema>> {
  const result = invoiceReviewSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: issues(result.error) };
}

/** Ret gerekçelidir; uyarılı onay "uyarıları gördüm" işareti ister (#1364, #1366). */
export function assertInvoiceReview(
  input: { action: InvoiceReviewAction; note: string | null; acknowledgeWarnings?: boolean },
  warnings: readonly MatchWarning[],
): void {
  if (input.action === 'rejected' && (input.note ?? '').trim().length < MIN_REJECT_NOTE) {
    throw new DomainError('REASON_REQUIRED');
  }
  if (input.action === 'approved' && warnings.length > 0 && input.acknowledgeWarnings !== true) {
    throw new DomainError('MATCH_WARNINGS_UNACKNOWLEDGED', warnings.join(','));
  }
}

/**
 * Faturanın kaynağı (ADR 0119 Ek 2026-10-24, #1500): tedarikçinin portaldan yüklediği ya da
 * portalı kullanmayan tedarikçi adına ofisin girdiği.
 */
export const INVOICE_SOURCES = ['portal', 'office'] as const;
export type InvoiceSource = (typeof INVOICE_SOURCES)[number];

/**
 * Ofis faturasında dört göz (#1500): giren kişi onaylayamaz (`SELF_APPROVAL_FORBIDDEN`; ödemedeki
 * kural, ADR 0119 §4). Yanlış girişini gerekçeyle reddedebilir. Portal faturası karşı taraftan
 * gelir, ek kural yok (ADR 0119 §3).
 */
export function assertOfficeInvoiceReview(
  invoice: { source: InvoiceSource; enteredBy: string },
  reviewer: string,
  action: InvoiceReviewAction,
): void {
  if (invoice.source === 'office' && action === 'approved' && invoice.enteredBy === reviewer) {
    throw new DomainError('SELF_APPROVAL_FORBIDDEN');
  }
}

// --- Ödeme ---

export const PAYMENT_KINDS = ['payment', 'reversal'] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];
export const PAYMENT_DECISIONS = ['approved', 'rejected'] as const;
export type PaymentDecision = (typeof PAYMENT_DECISIONS)[number];
export type PaymentStatus = 'pending' | PaymentDecision;

const paymentSchema = z.strictObject({
  currency: currencyField(),
  amount: moneyField(),
  valueDate: dayField(),
  reference: requiredText(80),
  note: optionalText(1000),
  invoiceId: z
    .uuid('INVALID_ID')
    .nullish()
    .transform((value) => value ?? null),
  bankAccountId: z.uuid(typed('INVALID_ID')),
});

export interface PaymentInput {
  currency: string;
  amount: bigint;
  valueDate: string;
  reference: string;
  note: string | null;
  invoiceId: string | null;
  bankAccountId: string;
}

/** Ödeme kaydı (ADR 0119 §4): tutar > 0, değer tarihi bugünden ileri olamaz. */
export function parsePaymentInput(input: unknown, today: string): ParseResult<PaymentInput> {
  const result = paymentSchema.safeParse(input);
  if (!result.success) return { ok: false, issues: issues(result.error) };
  const value = result.data;
  if (value.amount <= 0n) return { ok: false, issues: [{ path: 'amount', code: 'OUT_OF_RANGE' }] };
  if (value.valueDate > today) {
    return { ok: false, issues: [{ path: 'valueDate', code: 'FUTURE_DATE' }] };
  }
  return { ok: true, value };
}

const reversalSchema = z.strictObject({
  reason: requiredText(1000).pipe(z.string().min(MIN_LEDGER_REASON, 'REASON_TOO_SHORT')),
  valueDate: dayField().optional(),
});

/** Ters kayıt gerekçesi ≥ 10 karakter (#1372). */
export function parseReversal(
  input: unknown,
): ParseResult<{ reason: string; valueDate?: string | undefined }> {
  const result = reversalSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: issues(result.error) };
}

const decisionSchema = z
  .strictObject({
    action: z.enum(PAYMENT_DECISIONS, typed('INVALID_ACTION')),
    note: optionalText(1000),
  })
  .refine((value) => value.action !== 'rejected' || (value.note ?? '').length >= MIN_REJECT_NOTE, {
    message: 'REASON_REQUIRED',
    path: ['note'],
  });

export function parsePaymentDecision(
  input: unknown,
): ParseResult<{ action: PaymentDecision; note: string | null }> {
  const result = decisionSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: issues(result.error) };
}

/**
 * Ödemenin onayı (#1369, #1370): kaydeden onaylayamaz; banka ayrıntı sürümüne ilk ödemenin onayı
 * sürümün şirketteki onayından 24 saat sonra yapılabilir.
 */
export function assertPaymentApproval(input: {
  recordedBy: string;
  approver: string;
  bankApprovedAt: string | null;
  firstToVersion: boolean;
  now: string;
}): void {
  if (input.recordedBy === input.approver) throw new DomainError('SELF_APPROVAL_FORBIDDEN');
  if (input.firstToVersion && input.bankApprovedAt) {
    const ready = Date.parse(input.bankApprovedAt) + BANK_COOLING_OFF_HOURS * 3_600_000;
    if (Date.parse(input.now) < ready) {
      throw new DomainError('BANK_COOLING_OFF', new Date(ready).toISOString());
    }
  }
}

/** Ödeme ve ters kaydın içerik hash'ine giren kapalı liste. */
export function paymentContent(input: {
  supplierId: string;
  kind: PaymentKind;
  currency: string;
  amount: string;
  valueDate: string;
  reference: string;
  invoiceId: string | null;
  reversesPaymentId: string | null;
  bankAccountId: string | null;
  bankVersionId: string | null;
  bankDetailsHash: string | null;
  receiptSha256: string | null;
}) {
  return { ...input };
}

// --- Cari: bakiye, vade, ekstre ---

export interface LedgerInvoice {
  id: string;
  invoiceNo: string;
  currency: string;
  total: bigint;
  issueDate: string;
  dueDate: string;
  status: InvoiceStatus;
  createdAt: string;
}

export interface LedgerPayment {
  id: string;
  kind: PaymentKind;
  currency: string;
  amount: bigint;
  valueDate: string;
  reference: string;
  status: PaymentStatus;
  invoiceId: string | null;
  reversesId: string | null;
  createdAt: string;
}

/** Ödemenin bakiyeye etkisi: ödeme düşürür (+ ödenen), ters kayıt geri ekler (− ödenen). */
export function paymentEffect(payment: LedgerPayment): bigint {
  return payment.kind === 'reversal' ? -payment.amount : payment.amount;
}

export interface CurrencyBalance {
  currency: string;
  /** Onaylı faturalar. */
  invoiced: bigint;
  /** Onaylı ödemeler − onaylı ters kayıtlar. */
  paid: bigint;
  /** Borç: faturalanan − ödenen (eksi ise avans). */
  balance: bigint;
  pendingInvoices: bigint;
  pendingPayments: bigint;
}

/** Para birimi başına bakiye (ADR 0119 §5; #1374); kur dönüşümü yok. */
export function balancesOf(
  invoices: readonly LedgerInvoice[],
  payments: readonly LedgerPayment[],
): CurrencyBalance[] {
  const map = new Map<string, CurrencyBalance>();
  const entry = (currency: string) => {
    let value = map.get(currency);
    if (!value) {
      value = {
        currency,
        invoiced: 0n,
        paid: 0n,
        balance: 0n,
        pendingInvoices: 0n,
        pendingPayments: 0n,
      };
      map.set(currency, value);
    }
    return value;
  };
  for (const invoice of invoices) {
    if (invoice.status === 'approved') entry(invoice.currency).invoiced += invoice.total;
    else if (invoice.status === 'pending') entry(invoice.currency).pendingInvoices += invoice.total;
  }
  for (const payment of payments) {
    if (payment.status === 'approved') entry(payment.currency).paid += paymentEffect(payment);
    else if (payment.status === 'pending' && payment.kind === 'payment') {
      entry(payment.currency).pendingPayments += payment.amount;
    }
  }
  for (const value of map.values()) value.balance = value.invoiced - value.paid;
  return [...map.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

export interface AgingBuckets {
  notDue: bigint;
  d1_30: bigint;
  d31_60: bigint;
  d61_90: bigint;
  d90plus: bigint;
}

export interface CurrencyAging {
  currency: string;
  items: Array<{
    id: string;
    invoiceNo: string;
    dueDate: string;
    total: bigint;
    outstanding: bigint;
    daysOverdue: number;
  }>;
  buckets: AgingBuckets;
  overdue: bigint;
  /** Faturaya dağıtılamayan ödeme (avans). */
  unapplied: bigint;
}

/** Ters kaydı onaylanmış ödemeler: ikisi de dağıtımdan düşer. */
function reversedIds(payments: readonly LedgerPayment[]): Set<string> {
  return new Set(
    payments
      .filter((p) => p.kind === 'reversal' && p.status === 'approved' && p.reversesId)
      .map((p) => p.reversesId as string),
  );
}

const byDue = (a: LedgerInvoice, b: LedgerInvoice) =>
  a.dueDate.localeCompare(b.dueDate) ||
  a.issueDate.localeCompare(b.issueDate) ||
  a.createdAt.localeCompare(b.createdAt) ||
  a.id.localeCompare(b.id);

/**
 * Vade ve gecikme (#1375): onaylı ve ters kaydı olmayan ödemeler para birimi içinde önce bağlı
 * oldukları faturaya, kalanı FIFO ile en eski vadeli onaylı faturaya dağıtılır. Gecikme = bugün −
 * vade. Kovalar: vadesi gelmemiş, 1–30, 31–60, 61–90, 90+.
 */
export function agingOf(
  invoices: readonly LedgerInvoice[],
  payments: readonly LedgerPayment[],
  today: string,
): CurrencyAging[] {
  const reversed = reversedIds(payments);
  const currencies = new Set(
    invoices.filter((i) => i.status === 'approved').map((i) => i.currency),
  );
  for (const p of payments) if (p.status === 'approved') currencies.add(p.currency);
  const out: CurrencyAging[] = [];
  for (const currency of [...currencies].sort()) {
    const open = invoices
      .filter((i) => i.status === 'approved' && i.currency === currency)
      .sort(byDue)
      .map((i) => ({ invoice: i, outstanding: i.total }));
    const effective = payments
      .filter(
        (p) =>
          p.status === 'approved' &&
          p.kind === 'payment' &&
          p.currency === currency &&
          !reversed.has(p.id),
      )
      .sort(
        (a, b) =>
          a.valueDate.localeCompare(b.valueDate) ||
          a.createdAt.localeCompare(b.createdAt) ||
          a.id.localeCompare(b.id),
      );
    let pool = 0n;
    for (const payment of effective) {
      const target = payment.invoiceId
        ? open.find((item) => item.invoice.id === payment.invoiceId)
        : undefined;
      if (target) {
        const applied = payment.amount < target.outstanding ? payment.amount : target.outstanding;
        target.outstanding -= applied;
        pool += payment.amount - applied;
      } else {
        pool += payment.amount;
      }
    }
    for (const item of open) {
      if (pool <= 0n) break;
      const applied = pool < item.outstanding ? pool : item.outstanding;
      item.outstanding -= applied;
      pool -= applied;
    }
    const buckets: AgingBuckets = { notDue: 0n, d1_30: 0n, d31_60: 0n, d61_90: 0n, d90plus: 0n };
    let overdue = 0n;
    const items = open.map(({ invoice, outstanding }) => {
      const late = daysBetween(invoice.dueDate, today);
      const daysOverdue = late > 0 ? late : 0;
      if (outstanding > 0n) {
        if (daysOverdue === 0) buckets.notDue += outstanding;
        else {
          overdue += outstanding;
          if (daysOverdue <= 30) buckets.d1_30 += outstanding;
          else if (daysOverdue <= 60) buckets.d31_60 += outstanding;
          else if (daysOverdue <= 90) buckets.d61_90 += outstanding;
          else buckets.d90plus += outstanding;
        }
      }
      return {
        id: invoice.id,
        invoiceNo: invoice.invoiceNo,
        dueDate: invoice.dueDate,
        total: invoice.total,
        outstanding,
        daysOverdue,
      };
    });
    out.push({ currency, items, buckets, overdue, unapplied: pool });
  }
  return out;
}

export type StatementEntryKind = 'invoice' | 'payment' | 'reversal';

export interface StatementEntry {
  date: string;
  kind: StatementEntryKind;
  id: string;
  /** Fatura numarası ya da ödeme referansı. */
  reference: string;
  /** Borcu artıran (fatura, ters kayıt). */
  debit: bigint;
  /** Borcu azaltan (ödeme). */
  credit: bigint;
  balance: bigint;
  dueDate: string | null;
}

export interface Statement {
  currency: string;
  from: string;
  to: string;
  opening: bigint;
  entries: StatementEntry[];
  closing: bigint;
  totals: { debit: bigint; credit: bigint };
}

const KIND_ORDER: Record<StatementEntryKind, number> = { invoice: 0, payment: 1, reversal: 2 };

/**
 * Ekstre (#1376): yalnız onaylı kayıtlar; fatura fatura tarihinde, ödeme ve ters kayıt değer
 * tarihinde. `from` öncesi açılış bakiyesidir; aralıktaki hareketler yürüyen bakiyeyle.
 */
export function statementOf(
  invoices: readonly LedgerInvoice[],
  payments: readonly LedgerPayment[],
  range: { currency: string; from: string; to: string },
): Statement {
  const all: Array<Omit<StatementEntry, 'balance'> & { createdAt: string }> = [];
  for (const invoice of invoices) {
    if (invoice.status !== 'approved' || invoice.currency !== range.currency) continue;
    all.push({
      date: invoice.issueDate,
      kind: 'invoice',
      id: invoice.id,
      reference: invoice.invoiceNo,
      debit: invoice.total,
      credit: 0n,
      dueDate: invoice.dueDate,
      createdAt: invoice.createdAt,
    });
  }
  for (const payment of payments) {
    if (payment.status !== 'approved' || payment.currency !== range.currency) continue;
    const reversal = payment.kind === 'reversal';
    all.push({
      date: payment.valueDate,
      kind: payment.kind,
      id: payment.id,
      reference: payment.reference,
      debit: reversal ? payment.amount : 0n,
      credit: reversal ? 0n : payment.amount,
      dueDate: null,
      createdAt: payment.createdAt,
    });
  }
  all.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
      a.createdAt.localeCompare(b.createdAt) ||
      a.id.localeCompare(b.id),
  );
  let opening = 0n;
  let balance = 0n;
  const entries: StatementEntry[] = [];
  const totals = { debit: 0n, credit: 0n };
  for (const { createdAt: _createdAt, ...item } of all) {
    if (item.date < range.from) {
      opening += item.debit - item.credit;
      balance = opening;
      continue;
    }
    if (item.date > range.to) continue;
    balance += item.debit - item.credit;
    totals.debit += item.debit;
    totals.credit += item.credit;
    entries.push({ ...item, balance });
  }
  return {
    currency: range.currency,
    from: range.from,
    to: range.to,
    opening,
    entries,
    closing: opening + totals.debit - totals.credit,
    totals,
  };
}

// --- Geçmiş ---

export const RFQ_OUTCOMES = ['won', 'lost', 'cancelled', 'no_response', 'pending', 'open'] as const;
export type RfqOutcome = (typeof RFQ_OUTCOMES)[number];

/**
 * Tedarikçinin bir RFQ'daki sonucu (ADR 0119 §7): kendisine kesinleşmiş ödül ya da seçilen teklif
 * `won`; açık RFQ `open`; iptal `cancelled`; davetli ve teklifsiz `no_response`; başkasına karar
 * `lost`; karar yoksa `pending`. Rakip bilgisi girdi değildir.
 */
export function rfqOutcome(input: {
  invited: boolean;
  quoted: boolean;
  cancelled: boolean;
  decided: boolean;
  won: boolean;
  closed: boolean;
}): RfqOutcome {
  if (input.won) return 'won';
  if (input.cancelled) return 'cancelled';
  if (!input.closed && !input.decided) return 'open';
  if (!input.quoted) return 'no_response';
  return input.decided ? 'lost' : 'pending';
}

/**
 * Yanıt oranı (#1377): davet edildiği ve kapanmış RFQ'larda teklif verdiği / davet edildiği.
 * Açık pazardaki davetsiz teklifler ayrı sayılır.
 */
export function responseRateOf(
  rows: ReadonlyArray<{ invited: boolean; quoted: boolean; closed: boolean }>,
): { invited: number; responded: number; rate: number | null; openMarketQuotes: number } {
  let invited = 0;
  let responded = 0;
  let openMarketQuotes = 0;
  for (const row of rows) {
    if (!row.invited) {
      if (row.quoted) openMarketQuotes += 1;
      continue;
    }
    if (!row.closed) continue;
    invited += 1;
    if (row.quoted) responded += 1;
  }
  return { invited, responded, rate: invited === 0 ? null : responded / invited, openMarketQuotes };
}
